(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.documentElement.classList.add('pergola-js-ready');

  const setHeaderState = () => {
    document.body.classList.toggle('pergola-header-scrolled', window.scrollY > 24);
  };

  setHeaderState();
  window.addEventListener('scroll', setHeaderState, { passive: true });

  const closeMenuDrawer = (details, options = {}) => {
    if (!details) return;

    const drawer = details.closest('header-drawer');
    const summary = details.querySelector('summary');
    if (drawer && typeof drawer.closeMenuDrawer === 'function') {
      drawer.closeMenuDrawer(new Event('pergola:close-drawer'), options.restoreFocus === false ? false : summary);
    }

    details.classList.remove('menu-opening');
    details.querySelectorAll('details').forEach((nestedDetails) => {
      nestedDetails.classList.remove('menu-opening');
      nestedDetails.removeAttribute('open');
    });
    window.setTimeout(() => {
      details.removeAttribute('open');
    }, options.immediate ? 0 : 320);
    if (summary) {
      summary.setAttribute('aria-expanded', 'false');
      if (options.restoreFocus !== false) {
        summary.focus();
      }
    }
    document.body.classList.remove('overflow-hidden', 'overflow-hidden-mobile', 'overflow-hidden-tablet', 'overflow-hidden-desktop');
    document.documentElement.classList.remove('overflow-hidden');
  };

  let activeModal = null;
  let activeTrigger = null;

  const getModalVideo = (modal) => modal && modal.querySelector('video');

  const closeVideoModal = (modal = activeModal) => {
    if (!modal) return;

    const video = getModalVideo(modal);
    if (video) {
      video.pause();
      video.currentTime = 0;
    }

    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('pergola-modal-open');

    const hideModal = () => {
      if (!modal.classList.contains('is-open')) {
        modal.hidden = true;
      }
    };

    if (reduceMotion) {
      hideModal();
    } else {
      window.setTimeout(hideModal, 220);
    }

    if (activeTrigger) {
      activeTrigger.focus();
    }

    activeModal = null;
    activeTrigger = null;
  };

  const openVideoModal = (trigger) => {
    const modalId = trigger.getAttribute('data-pergola-video-trigger');
    const modal = modalId ? document.getElementById(modalId) : null;
    if (!modal) return;

    activeModal = modal;
    activeTrigger = trigger;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    window.requestAnimationFrame(() => {
      modal.classList.add('is-open');
    });
    document.body.classList.add('pergola-modal-open');

    const video = getModalVideo(modal);
    if (video) {
      video.currentTime = 0;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }

    const closeButton = modal.querySelector('[data-pergola-video-close]');
    if (closeButton) {
      closeButton.focus();
    }
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : event.target.parentElement;
    if (!target) return;

    const drawerCloseButton = target.closest('.js-menu-drawer-close');
    if (drawerCloseButton) {
      event.preventDefault();
      closeMenuDrawer(drawerCloseButton.closest('details'));
      return;
    }

    const drawerLink = target.closest('.menu-drawer a');
    if (drawerLink) {
      const drawerDetails = drawerLink.closest('details');
      window.setTimeout(() => closeMenuDrawer(drawerDetails, { restoreFocus: false }), 40);
    }

    const drawerBackdrop = target.matches('.menu-drawer') ? target : null;
    if (drawerBackdrop) {
      closeMenuDrawer(drawerBackdrop.closest('details'));
      return;
    }

    const trigger = target.closest('[data-pergola-video-trigger]');
    if (trigger) {
      event.preventDefault();
      openVideoModal(trigger);
      return;
    }

    const closeButton = target.closest('[data-pergola-video-close]');
    if (closeButton) {
      closeVideoModal(closeButton.closest('[data-pergola-video-modal]'));
      return;
    }

    const modal = target.matches('[data-pergola-video-modal]') ? target : null;
    if (modal) {
      closeVideoModal(modal);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activeModal) {
      closeVideoModal(activeModal);
    }

    if (event.key === 'Escape') {
      const openDrawer = document.querySelector('header-drawer details[open]');
      if (openDrawer) {
        closeMenuDrawer(openDrawer);
      }
    }
  });

  const serviceCarouselMedia = window.matchMedia('(max-width: 749px)');

  const resetServiceCarousel = (carousel) => {
    carousel.dataset.carouselInitialized = 'false';
    carousel.classList.remove('is-dragging');
    carousel.querySelectorAll('[data-carousel-card]').forEach((card) => {
      card.classList.remove('is-active', 'is-prev', 'is-next', 'is-hidden');
      card.removeAttribute('aria-hidden');
    });
  };

  const initServiceCarousels = (root = document) => {
    root.querySelectorAll('[data-service-carousel]').forEach((carousel) => {
      if (!serviceCarouselMedia.matches) {
        resetServiceCarousel(carousel);
        return;
      }

      if (carousel.dataset.carouselInitialized === 'true') return;

      const cards = Array.from(carousel.querySelectorAll('[data-carousel-card]'));
      if (!cards.length) return;

      const prevButton = carousel.querySelector('[data-service-carousel-prev]');
      const nextButton = carousel.querySelector('[data-service-carousel-next]');
      let activeIndex = 0;
      let startX = 0;
      let startY = 0;
      let currentX = 0;
      let currentY = 0;
      let isPointerDown = false;
      let isHorizontalDrag = false;

      const getPreviousIndex = () => (activeIndex - 1 + cards.length) % cards.length;
      const getNextIndex = () => (activeIndex + 1) % cards.length;

      const updateCards = () => {
        const previousIndex = getPreviousIndex();
        const nextIndex = getNextIndex();

        cards.forEach((card, index) => {
          card.classList.toggle('is-active', index === activeIndex);
          card.classList.toggle('is-prev', index === previousIndex);
          card.classList.toggle('is-next', index === nextIndex);
          card.classList.toggle('is-hidden', index !== activeIndex && index !== previousIndex && index !== nextIndex);
          card.setAttribute('aria-hidden', index === activeIndex ? 'false' : 'true');
        });
      };

      const goToCard = (index) => {
        activeIndex = (index + cards.length) % cards.length;
        updateCards();
      };

      const goPrevious = () => goToCard(activeIndex - 1);
      const goNext = () => goToCard(activeIndex + 1);

      prevButton?.addEventListener('click', goPrevious);
      nextButton?.addEventListener('click', goNext);

      carousel.addEventListener('pointerdown', (event) => {
        if (event.target.closest('button')) return;
        if (event.button !== 0) return;

        isPointerDown = true;
        isHorizontalDrag = false;
        startX = event.clientX;
        startY = event.clientY;
        currentX = event.clientX;
        currentY = event.clientY;
        carousel.classList.add('is-dragging');
        carousel.setPointerCapture?.(event.pointerId);
      });

      carousel.addEventListener('pointermove', (event) => {
        if (!isPointerDown) return;

        currentX = event.clientX;
        currentY = event.clientY;
        const deltaX = currentX - startX;
        const deltaY = event.clientY - startY;

        if (!isHorizontalDrag && Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY)) {
          isHorizontalDrag = true;
        }

        if (isHorizontalDrag) {
          event.preventDefault();
        }
      });

      const endDrag = (event) => {
        if (!isPointerDown) return;

        if (event) {
          currentX = event.clientX;
          currentY = event.clientY;
        }

        const deltaX = currentX - startX;
        const deltaY = currentY - startY;
        if (Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY)) {
          if (deltaX < 0) {
            goNext();
          } else {
            goPrevious();
          }
        }

        isPointerDown = false;
        isHorizontalDrag = false;
        carousel.classList.remove('is-dragging');
      };

      carousel.addEventListener('pointerup', endDrag);
      carousel.addEventListener('pointercancel', endDrag);
      carousel.addEventListener('pointerleave', endDrag);
      document.addEventListener('pointerup', endDrag);
      document.addEventListener('pointercancel', endDrag);

      carousel.dataset.carouselInitialized = 'true';
      updateCards();
    });
  };

  initServiceCarousels();
  serviceCarouselMedia.addEventListener?.('change', () => initServiceCarousels());

  const galleryCarouselMedia = window.matchMedia('(max-width: 749px)');

  const resetGalleryCarousel = (carousel) => {
    carousel.dataset.galleryCarouselInitialized = 'false';
    carousel.classList.remove('is-dragging');
    carousel.querySelectorAll('[data-gallery-card]').forEach((card) => {
      card.classList.remove('is-active', 'is-prev', 'is-next', 'is-hidden');
      card.removeAttribute('aria-hidden');
    });
  };

  const initGalleryCarousels = (root = document) => {
    root.querySelectorAll('[data-gallery-carousel]').forEach((carousel) => {
      if (!galleryCarouselMedia.matches) {
        resetGalleryCarousel(carousel);
        return;
      }

      if (carousel.dataset.galleryCarouselInitialized === 'true') return;

      const cards = Array.from(carousel.querySelectorAll('[data-gallery-card]'));
      if (!cards.length) return;

      const prevButton = carousel.querySelector('[data-gallery-carousel-prev]');
      const nextButton = carousel.querySelector('[data-gallery-carousel-next]');
      let activeIndex = 0;
      let startX = 0;
      let startY = 0;
      let currentX = 0;
      let currentY = 0;
      let isPointerDown = false;
      let isHorizontalDrag = false;
      let dragged = false;

      const getPreviousIndex = () => (activeIndex - 1 + cards.length) % cards.length;
      const getNextIndex = () => (activeIndex + 1) % cards.length;

      const updateCards = () => {
        const previousIndex = cards.length > 1 ? getPreviousIndex() : -1;
        const nextIndex = cards.length > 2 ? getNextIndex() : cards.length === 2 ? getNextIndex() : -1;

        cards.forEach((card, index) => {
          card.classList.toggle('is-active', index === activeIndex);
          card.classList.toggle('is-prev', index === previousIndex && index !== activeIndex);
          card.classList.toggle('is-next', index === nextIndex && index !== activeIndex && index !== previousIndex);
          card.classList.toggle('is-hidden', index !== activeIndex && index !== previousIndex && index !== nextIndex);
          card.setAttribute('aria-hidden', index === activeIndex ? 'false' : 'true');
        });
      };

      const goToCard = (index) => {
        activeIndex = (index + cards.length) % cards.length;
        updateCards();
      };

      const goPrevious = () => goToCard(activeIndex - 1);
      const goNext = () => goToCard(activeIndex + 1);

      prevButton?.addEventListener('click', goPrevious);
      nextButton?.addEventListener('click', goNext);

      carousel.addEventListener('pointerdown', (event) => {
        if (event.target.closest('button')) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;

        isPointerDown = true;
        isHorizontalDrag = false;
        dragged = false;
        startX = event.clientX;
        startY = event.clientY;
        currentX = event.clientX;
        currentY = event.clientY;
        carousel.classList.add('is-dragging');
        try {
          carousel.setPointerCapture?.(event.pointerId);
        } catch (error) {
          // Pointer capture can fail in embedded previews; document listeners still finish the drag.
        }
      });

      carousel.addEventListener('pointermove', (event) => {
        if (!isPointerDown) return;

        currentX = event.clientX;
        currentY = event.clientY;
        const deltaX = currentX - startX;
        const deltaY = event.clientY - startY;

        if (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8) {
          dragged = true;
        }

        if (!isHorizontalDrag && Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY)) {
          isHorizontalDrag = true;
        }

        if (isHorizontalDrag) {
          event.preventDefault();
        }
      });

      const endDrag = (event) => {
        if (!isPointerDown) return;

        if (event) {
          currentX = event.clientX;
          currentY = event.clientY;
        }

        const deltaX = currentX - startX;
        const deltaY = currentY - startY;
        if (Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY)) {
          if (deltaX < 0) {
            goNext();
          } else {
            goPrevious();
          }
        }

        isPointerDown = false;
        isHorizontalDrag = false;
        carousel.classList.remove('is-dragging');
      };

      carousel.addEventListener(
        'click',
        (event) => {
          if (!dragged) return;
          event.preventDefault();
          event.stopPropagation();
          dragged = false;
        },
        true
      );

      carousel.addEventListener('pointerup', endDrag);
      carousel.addEventListener('pointercancel', endDrag);
      carousel.addEventListener('pointerleave', endDrag);
      document.addEventListener('pointerup', endDrag);
      document.addEventListener('pointercancel', endDrag);

      carousel.dataset.galleryCarouselInitialized = 'true';
      updateCards();
    });
  };

  initGalleryCarousels();
  galleryCarouselMedia.addEventListener?.('change', () => initGalleryCarousels());

  const initGalleryPreviewLinks = (root = document) => {
    root.querySelectorAll('[data-gallery-preview]').forEach((gallery) => {
      if (gallery.dataset.galleryPreviewLinksInitialized === 'true') return;

      let isPointerDown = false;
      let startX = 0;
      let startY = 0;
      let suppressClick = false;

      gallery.addEventListener('pointerdown', (event) => {
        if (!event.target.closest('[data-gallery-preview-link]')) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;

        isPointerDown = true;
        suppressClick = false;
        startX = event.clientX;
        startY = event.clientY;
      });

      gallery.addEventListener('pointermove', (event) => {
        if (!isPointerDown) return;

        const deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;
        if (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8) {
          suppressClick = true;
        }
      });

      const finishPointer = () => {
        isPointerDown = false;
      };

      gallery.addEventListener('pointerup', finishPointer);
      gallery.addEventListener('pointercancel', finishPointer);

      gallery.addEventListener(
        'click',
        (event) => {
          if (!suppressClick || !event.target.closest('[data-gallery-preview-link]')) return;
          event.preventDefault();
          event.stopPropagation();
          suppressClick = false;
        },
        true
      );

      gallery.dataset.galleryPreviewLinksInitialized = 'true';
    });
  };

  const initGalleryLightbox = () => {
    if (document.documentElement.dataset.galleryLightboxInitialized === 'true') return;

    let activeModal = null;
    let activeTriggers = [];
    let activeIndex = 0;
    let previousFocus = null;
    let touchStartX = 0;
    let touchStartY = 0;
    let isLightboxPointerDown = false;

    const updateLightbox = () => {
      if (!activeModal || !activeTriggers.length) return;

      const trigger = activeTriggers[activeIndex];
      const image = activeModal.querySelector('[data-gallery-lightbox-image]');
      const title = activeModal.querySelector('[data-gallery-lightbox-title]');
      const location = activeModal.querySelector('[data-gallery-lightbox-location]');
      const counter = activeModal.querySelector('[data-gallery-lightbox-counter]');
      const previousButton = activeModal.querySelector('[data-gallery-lightbox-previous]');
      const nextButton = activeModal.querySelector('[data-gallery-lightbox-next]');
      const isSingleImage = activeTriggers.length === 1;

      image.src = trigger.dataset.fullImage || '';
      image.alt = trigger.dataset.imageAlt || '';
      title.textContent = trigger.dataset.projectTitle || '';
      location.textContent = trigger.dataset.projectLocation || '';
      location.hidden = !trigger.dataset.projectLocation;
      counter.textContent = `${activeIndex + 1} / ${activeTriggers.length}`;
      previousButton.hidden = isSingleImage;
      nextButton.hidden = isSingleImage;
    };

    const showPreviousImage = () => {
      if (activeTriggers.length < 2) return;
      activeIndex = (activeIndex - 1 + activeTriggers.length) % activeTriggers.length;
      updateLightbox();
    };

    const showNextImage = () => {
      if (activeTriggers.length < 2) return;
      activeIndex = (activeIndex + 1) % activeTriggers.length;
      updateLightbox();
    };

    const closeLightbox = () => {
      if (!activeModal) return;

      const modalToClose = activeModal;
      const image = modalToClose.querySelector('[data-gallery-lightbox-image]');
      modalToClose.classList.remove('is-visible');
      document.body.classList.remove('apex-gallery-lightbox-open');

      const finishClose = () => {
        modalToClose.hidden = true;
        image.removeAttribute('src');
        image.alt = '';
      };

      if (reduceMotion) {
        finishClose();
      } else {
        window.setTimeout(finishClose, 260);
      }

      activeModal = null;
      activeTriggers = [];
      previousFocus?.focus();
      previousFocus = null;
    };

    const openLightbox = (trigger) => {
      const galleryPage = trigger.closest('[data-gallery-page]');
      const modal = galleryPage?.querySelector('[data-gallery-lightbox]');
      if (!galleryPage || !modal) return;

      const group = trigger.dataset.galleryGroup;
      activeTriggers = Array.from(galleryPage.querySelectorAll('[data-gallery-lightbox-trigger]')).filter(
        (item) => item.dataset.galleryGroup === group
      );
      activeIndex = activeTriggers.indexOf(trigger);
      if (activeIndex < 0 || !activeTriggers.length) return;

      activeModal = modal;
      previousFocus = trigger;
      updateLightbox();
      modal.hidden = false;
      document.body.classList.add('apex-gallery-lightbox-open');
      window.requestAnimationFrame(() => {
        modal.classList.add('is-visible');
        modal.querySelector('[data-gallery-lightbox-close]')?.focus();
      });
    };

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-gallery-lightbox-trigger]');
      if (trigger) {
        event.preventDefault();
        openLightbox(trigger);
        return;
      }

      if (!activeModal) return;
      if (event.target.closest('[data-gallery-lightbox-close]')) {
        closeLightbox();
      } else if (event.target.closest('[data-gallery-lightbox-previous]')) {
        showPreviousImage();
      } else if (event.target.closest('[data-gallery-lightbox-next]')) {
        showNextImage();
      } else if (event.target === activeModal) {
        closeLightbox();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (!activeModal) return;

      if (event.key === 'Escape') {
        closeLightbox();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        showPreviousImage();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        showNextImage();
      }
    });

    document.addEventListener('pointerdown', (event) => {
      if (!activeModal || !event.target.closest('[data-gallery-lightbox-stage]')) return;
      isLightboxPointerDown = true;
      touchStartX = event.clientX;
      touchStartY = event.clientY;
    });

    document.addEventListener('pointerup', (event) => {
      if (!activeModal || !isLightboxPointerDown) return;
      isLightboxPointerDown = false;

      const deltaX = event.clientX - touchStartX;
      const deltaY = event.clientY - touchStartY;
      if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY)) return;

      if (deltaX < 0) {
        showNextImage();
      } else {
        showPreviousImage();
      }
    });

    document.addEventListener('pointercancel', () => {
      isLightboxPointerDown = false;
    });

    document.documentElement.dataset.galleryLightboxInitialized = 'true';
  };

  initGalleryPreviewLinks();
  initGalleryLightbox();

  let reviewTickerPauseTimer = null;

  const pauseReviewTicker = (ticker) => {
    if (!ticker) return;
    ticker.classList.add('is-paused');
    window.clearTimeout(reviewTickerPauseTimer);
  };

  const resumeReviewTickerSoon = (ticker) => {
    if (!ticker) return;
    window.clearTimeout(reviewTickerPauseTimer);
    reviewTickerPauseTimer = window.setTimeout(() => {
      ticker.classList.remove('is-paused');
    }, 1400);
  };

  document.addEventListener(
    'touchstart',
    (event) => {
      pauseReviewTicker(event.target instanceof Element ? event.target.closest('[data-review-ticker]') : null);
    },
    { passive: true }
  );

  document.addEventListener(
    'touchend',
    (event) => {
      resumeReviewTickerSoon(event.target instanceof Element ? event.target.closest('[data-review-ticker]') : null);
    },
    { passive: true }
  );

  const processAccordionMedia = window.matchMedia('(max-width: 749px)');

  const setProcessStepOpen = (step, isOpen) => {
    const trigger = step.querySelector('.pergola-process-step__trigger');
    step.classList.toggle('is-open', isOpen);
    if (trigger) {
      trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }
  };

  const initProcessAccordions = (root = document) => {
    root.querySelectorAll('.pergola-process-list').forEach((list) => {
      const steps = Array.from(list.querySelectorAll('[data-process-step]'));
      if (!steps.length) return;

      if (!processAccordionMedia.matches) {
        steps.forEach((step) => {
          const trigger = step.querySelector('.pergola-process-step__trigger');
          if (trigger) {
            trigger.setAttribute('aria-expanded', step.classList.contains('is-open') ? 'true' : 'false');
          }
        });
        return;
      }

      const openOnly = (targetStep) => {
        steps.forEach((step) => setProcessStepOpen(step, step === targetStep));
      };

      if (list.dataset.processAccordionInitialized !== 'true') {
        steps.forEach((step) => {
          const trigger = step.querySelector('.pergola-process-step__trigger');
          if (!trigger) return;

          trigger.addEventListener('click', () => {
            openOnly(step);
          });
        });

        list.dataset.processAccordionInitialized = 'true';
      }

      openOnly(steps.find((step) => step.classList.contains('is-open')) || steps[0]);
    });
  };

  initProcessAccordions();
  processAccordionMedia.addEventListener?.('change', () => initProcessAccordions());

  const setCustomOptionOpen = (item, isOpen) => {
    const trigger = item.querySelector('.pergola-options__mobile-trigger');
    item.classList.toggle('is-open', isOpen);
    if (trigger) {
      trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }
  };

  const initCustomOptions = (root = document) => {
    root.querySelectorAll('[data-custom-options]').forEach((section) => {
      const hotspots = Array.from(section.querySelectorAll('[data-options-hotspot]'));
      const panels = Array.from(section.querySelectorAll('[data-options-panel]'));
      const panelTarget = section.querySelector('[data-options-panel-target]');
      const mobileItems = Array.from(section.querySelectorAll('[data-options-mobile-item]'));
      let activeHotspotIndex = -1;
      let panelLeaveTimer = null;
      let panelClearTimer = null;

      const activateHotspot = (targetIndex) => {
        if (targetIndex === activeHotspotIndex) return;
        const previousHotspotIndex = activeHotspotIndex;
        activeHotspotIndex = targetIndex;
        window.clearTimeout(panelLeaveTimer);
        window.clearTimeout(panelClearTimer);
        panelTarget?.classList.remove('is-leaving', 'is-entering', 'is-visible', 'is-swapping');

        hotspots.forEach((hotspot, index) => {
          const isActive = index === targetIndex;
          hotspot.classList.toggle('is-active', isActive);
          hotspot.setAttribute('aria-expanded', isActive ? 'true' : 'false');
        });

        const updatePanelState = () => {
          panels.forEach((panel, index) => {
            panel.classList.toggle('is-active', index === targetIndex);
          });
        };

        if (previousHotspotIndex < 0 || !panelTarget || reduceMotion) {
          updatePanelState();
          panelTarget?.classList.remove('is-leaving', 'is-entering', 'is-visible', 'is-swapping');
        } else {
          panelTarget.classList.add('is-leaving', 'is-swapping');
          panelLeaveTimer = window.setTimeout(() => {
            updatePanelState();
            panelTarget.classList.remove('is-leaving');
            panelTarget.classList.add('is-entering');
            void panelTarget.offsetHeight;

            window.requestAnimationFrame(() => {
              panelTarget.classList.remove('is-entering');
              panelTarget.classList.add('is-visible');
            });

            panelClearTimer = window.setTimeout(() => {
              panelTarget.classList.remove('is-visible', 'is-swapping');
            }, 600);
          }, 220);
        }
      };

      if (section.dataset.customOptionsHotspotsInitialized !== 'true') {
        hotspots.forEach((hotspot, index) => {
          hotspot.addEventListener('pointerenter', () => activateHotspot(index));
          hotspot.addEventListener('click', () => activateHotspot(index));
          hotspot.addEventListener('focus', () => activateHotspot(index));
        });
        section.dataset.customOptionsHotspotsInitialized = 'true';
      }

      if (hotspots.length) {
        const initialIndex = Math.max(0, hotspots.findIndex((hotspot) => hotspot.classList.contains('is-active')));
        activateHotspot(initialIndex);
      }

      if (section.dataset.customOptionsMobileInitialized !== 'true') {
        mobileItems.forEach((item) => {
          const trigger = item.querySelector('.pergola-options__mobile-trigger');
          if (!trigger) return;

          trigger.addEventListener('click', () => {
            mobileItems.forEach((otherItem) => setCustomOptionOpen(otherItem, otherItem === item));
          });
        });
        section.dataset.customOptionsMobileInitialized = 'true';
      }

      if (mobileItems.length && !mobileItems.some((item) => item.classList.contains('is-open'))) {
        setCustomOptionOpen(mobileItems[0], true);
      }
    });
  };

  initCustomOptions();

  const normalizeHexColor = (value) => {
    if (!value) return '#111111';
    const hex = value.trim();
    const normalized = hex.charAt(0) === '#' ? hex : `#${hex}`;
    return /^#[0-9A-Fa-f]{6}$/.test(normalized) ? normalized.toUpperCase() : '#111111';
  };

  const hslToHex = (hue, saturation, lightness) => {
    const s = saturation / 100;
    const l = lightness / 100;
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
    const m = l - c / 2;
    let red = 0;
    let green = 0;
    let blue = 0;

    if (hue >= 0 && hue < 60) {
      red = c;
      green = x;
    } else if (hue >= 60 && hue < 120) {
      red = x;
      green = c;
    } else if (hue >= 120 && hue < 180) {
      green = c;
      blue = x;
    } else if (hue >= 180 && hue < 240) {
      green = x;
      blue = c;
    } else if (hue >= 240 && hue < 300) {
      red = x;
      blue = c;
    } else {
      red = c;
      blue = x;
    }

    const toHex = (channel) => {
      const value = Math.round((channel + m) * 255).toString(16);
      return value.padStart(2, '0');
    };

    return `#${toHex(red)}${toHex(green)}${toHex(blue)}`.toUpperCase();
  };

  const hexToHsl = (hexColor) => {
    const hex = normalizeHexColor(hexColor).replace('#', '');
    const red = parseInt(hex.slice(0, 2), 16) / 255;
    const green = parseInt(hex.slice(2, 4), 16) / 255;
    const blue = parseInt(hex.slice(4, 6), 16) / 255;
    const max = Math.max(red, green, blue);
    const min = Math.min(red, green, blue);
    const delta = max - min;
    let hue = 0;

    if (delta !== 0) {
      if (max === red) {
        hue = 60 * (((green - blue) / delta) % 6);
      } else if (max === green) {
        hue = 60 * ((blue - red) / delta + 2);
      } else {
        hue = 60 * ((red - green) / delta + 4);
      }
    }

    if (hue < 0) hue += 360;

    const lightness = (max + min) / 2;
    const saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));

    return {
      hue,
      saturation: saturation * 100,
      lightness: lightness * 100
    };
  };

  const getRgbFromHex = (hexColor) => {
    const hex = normalizeHexColor(hexColor).replace('#', '');
    return {
      red: parseInt(hex.slice(0, 2), 16),
      green: parseInt(hex.slice(2, 4), 16),
      blue: parseInt(hex.slice(4, 6), 16)
    };
  };

  const getRelativeLuminance = (hexColor) => {
    const { red, green, blue } = getRgbFromHex(hexColor);
    const channels = [red, green, blue].map((value) => {
      const channel = value / 255;
      return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
    });

    return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
  };

  const getContrastRatio = (luminanceA, luminanceB) => {
    const lighter = Math.max(luminanceA, luminanceB);
    const darker = Math.min(luminanceA, luminanceB);
    return (lighter + 0.05) / (darker + 0.05);
  };

  const getQuoteColorContrast = (hexColor) => {
    const luminance = getRelativeLuminance(hexColor);
    const contrastWithBlack = getContrastRatio(luminance, 0);
    const contrastWithWhite = getContrastRatio(luminance, 1);
    const { saturation } = hexToHsl(hexColor);
    const isBrightSaturated = luminance > 0.38 && saturation > 58;
    const useDarkText = contrastWithBlack >= contrastWithWhite;

    return useDarkText
      ? {
          tone: isBrightSaturated ? 'bright' : 'light',
          panelText: '#111111',
          panelMuted: 'rgba(17, 17, 17, 0.62)',
          panelLabel: 'rgba(17, 17, 17, 0.78)',
          panelBorder: 'rgba(17, 17, 17, 0.12)',
          buttonBg: '#111111',
          buttonText: '#ffffff',
          buttonBorder: '#111111',
          buttonHoverBg: '#e5231f',
          buttonHoverText: '#ffffff',
          buttonHoverBorder: '#e5231f'
        }
      : {
          tone: 'dark',
          panelText: '#ffffff',
          panelMuted: 'rgba(255, 255, 255, 0.74)',
          panelLabel: 'rgba(255, 255, 255, 0.88)',
          panelBorder: 'rgba(255, 255, 255, 0.28)',
          buttonBg: '#ffffff',
          buttonText: '#111111',
          buttonBorder: '#ffffff',
          buttonHoverBg: '#e5231f',
          buttonHoverText: '#ffffff',
          buttonHoverBorder: '#e5231f'
        };
  };

  const initQuoteColorSelectors = (root = document) => {
    root.querySelectorAll('[data-quote-color-selector]').forEach((selector) => {
      if (selector.dataset.quoteColorInitialized === 'true') return;

      const section = selector.closest('.pergola-quote-form-section');
      const form = section?.querySelector('.pergola-quote-form');
      const presets = Array.from(selector.querySelectorAll('[data-quote-color]'));
      const colorWheel = selector.querySelector('[data-quote-color-wheel]');
      const colorInput = selector.querySelector('[data-quote-color-input]');
      const selectedHex = selector.querySelector('[data-quote-selected-hex]');
      const selectedChip = selector.querySelector('[data-quote-selected-chip]');
      const selectedInput = section?.querySelector('[data-quote-selected-input]');
      const preferredColorSelect = section?.querySelector('select[name="contact[Preferred Color]"]');

      if (!form) return;

      const featuresSummaryInput = form.querySelector('[data-features-summary]');
      const quoteBodyInput = form.querySelector('[data-quote-body]');
      const emailInput = form.querySelector('input[name="contact[email]"]');
      const clientError = form.querySelector('[data-quote-client-error]');
      const clientErrorText = clientError?.querySelector('[data-quote-error-text]');
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      let isDraggingWheel = false;

      const setActivePreset = (activePreset) => {
        presets.forEach((preset) => {
          const isActive = preset === activePreset;
          preset.classList.toggle('is-active', isActive);
          preset.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
      };

      const setWheelPositionFromColor = (color) => {
        if (!colorWheel) return;

        const { hue, saturation } = hexToHsl(color);
        const angle = ((hue - 90) * Math.PI) / 180;
        const radius = Math.min(46, Math.max(0, saturation * 0.46));
        const x = 50 + Math.cos(angle) * radius;
        const y = 50 + Math.sin(angle) * radius;

        colorWheel.style.setProperty('--wheel-handle-x', `${x}%`);
        colorWheel.style.setProperty('--wheel-handle-y', `${y}%`);
      };

      const applyColor = (color, activePreset = null, options = {}) => {
        const normalizedColor = normalizeHexColor(color);
        const contrast = getQuoteColorContrast(normalizedColor);

        selector.style.setProperty('--selected-quote-color', normalizedColor);
        form.style.setProperty('--selected-quote-color', normalizedColor);
        form.style.setProperty('--quote-bg', normalizedColor);
        form.style.setProperty('--quote-panel-bg', normalizedColor);
        form.style.setProperty('--quote-panel-text', contrast.panelText);
        form.style.setProperty('--quote-panel-muted', contrast.panelMuted);
        form.style.setProperty('--quote-panel-label', contrast.panelLabel);
        form.style.setProperty('--quote-panel-border', contrast.panelBorder);
        form.style.setProperty('--quote-text', contrast.panelText);
        form.style.setProperty('--quote-muted', contrast.panelMuted);
        form.style.setProperty('--quote-button-bg', contrast.buttonBg);
        form.style.setProperty('--quote-button-text', contrast.buttonText);
        form.style.setProperty('--quote-button-border', contrast.buttonBorder);
        form.style.setProperty('--quote-button-hover-bg', contrast.buttonHoverBg);
        form.style.setProperty('--quote-button-hover-text', contrast.buttonHoverText);
        form.style.setProperty('--quote-button-hover-border', contrast.buttonHoverBorder);
        form.classList.toggle('is-light-color', contrast.tone === 'light' || contrast.tone === 'bright');
        form.classList.toggle('is-dark-color', contrast.tone === 'dark');
        form.classList.toggle('is-bright-color', contrast.tone === 'bright');
        form.classList.toggle('is-light-selected-color', contrast.tone === 'light' || contrast.tone === 'bright');
        form.classList.toggle('is-dark-selected-color', contrast.tone === 'dark');

        if (colorWheel) {
          colorWheel.style.setProperty('--wheel-selected-color', normalizedColor);
          colorWheel.setAttribute('aria-valuetext', normalizedColor);
          if (!options.skipWheelPosition) {
            setWheelPositionFromColor(normalizedColor);
          }
        }

        if (selectedHex) {
          selectedHex.textContent = normalizedColor;
        }

        if (selectedChip) {
          selectedChip.style.setProperty('--selected-preview-color', normalizedColor);
        }

        if (selectedInput) {
          selectedInput.value = normalizedColor;
        }

        if (colorInput && colorInput.value.toUpperCase() !== normalizedColor) {
          colorInput.value = normalizedColor;
        }

        if (preferredColorSelect) {
          preferredColorSelect.value = 'Custom';
        }

        setActivePreset(activePreset);
      };

      const updateFromWheelPointer = (event) => {
        if (!colorWheel) return;

        const rect = colorWheel.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const deltaX = event.clientX - centerX;
        const deltaY = event.clientY - centerY;
        const radius = rect.width / 2;
        const distance = Math.min(radius, Math.sqrt(deltaX * deltaX + deltaY * deltaY));
        const rawDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        const clampedScale = rawDistance > radius ? radius / rawDistance : 1;
        const clampedX = deltaX * clampedScale;
        const clampedY = deltaY * clampedScale;
        const hue = (Math.atan2(deltaY, deltaX) * 180) / Math.PI + 90;
        const normalizedHue = (hue + 360) % 360;
        const saturation = Math.max(0, Math.min(100, (distance / radius) * 100));
        const lightness = 52 - saturation * 0.08;
        const x = 50 + (clampedX / radius) * 50;
        const y = 50 + (clampedY / radius) * 50;
        const color = hslToHex(normalizedHue, saturation, lightness);

        colorWheel.style.setProperty('--wheel-handle-x', `${Math.max(0, Math.min(100, x))}%`);
        colorWheel.style.setProperty('--wheel-handle-y', `${Math.max(0, Math.min(100, y))}%`);
        applyColor(color, null, { skipWheelPosition: true });
      };

      presets.forEach((preset) => {
        preset.addEventListener('click', () => {
          applyColor(preset.dataset.quoteColor, preset);
        });
      });

      colorWheel?.addEventListener('pointerdown', (event) => {
        isDraggingWheel = true;
        colorWheel.setPointerCapture?.(event.pointerId);
        updateFromWheelPointer(event);
      });

      colorWheel?.addEventListener('pointermove', (event) => {
        if (!isDraggingWheel) return;
        updateFromWheelPointer(event);
      });

      const stopWheelDrag = (event) => {
        isDraggingWheel = false;
        colorWheel?.releasePointerCapture?.(event.pointerId);
      };

      colorWheel?.addEventListener('pointerup', stopWheelDrag);
      colorWheel?.addEventListener('pointercancel', stopWheelDrag);
      colorWheel?.addEventListener('keydown', (event) => {
        const current = hexToHsl(colorInput?.value || '#111111');
        let nextHue = current.hue;
        let nextSaturation = current.saturation;
        const hueStep = event.shiftKey ? 15 : 5;
        const saturationStep = event.shiftKey ? 10 : 5;

        if (event.key === 'ArrowRight') {
          nextHue += hueStep;
        } else if (event.key === 'ArrowLeft') {
          nextHue -= hueStep;
        } else if (event.key === 'ArrowUp') {
          nextSaturation += saturationStep;
        } else if (event.key === 'ArrowDown') {
          nextSaturation -= saturationStep;
        } else {
          return;
        }

        event.preventDefault();
        const color = hslToHex((nextHue + 360) % 360, Math.max(0, Math.min(100, nextSaturation)), 52 - Math.max(0, Math.min(100, nextSaturation)) * 0.08);
        applyColor(color, null);
      });

      colorInput?.addEventListener('input', (event) => {
        applyColor(event.target.value, null);
      });

      if (quoteBodyInput && form.dataset.quoteSummaryInitialized !== 'true') {
        const showEmailError = () => {
          if (clientErrorText) {
            clientErrorText.textContent = 'Please enter a valid email address before submitting your quote request.';
          }

          if (clientError) {
            clientError.hidden = false;
          }

          emailInput?.setAttribute('aria-invalid', 'true');
          emailInput?.focus({ preventScroll: false });
        };

        const clearEmailError = () => {
          if (clientError) {
            clientError.hidden = true;
          }

          emailInput?.removeAttribute('aria-invalid');
        };

        emailInput?.addEventListener('invalid', (event) => {
          event.preventDefault();
          showEmailError();
        });

        emailInput?.addEventListener('input', () => {
          if (emailPattern.test(emailInput.value.trim())) {
            clearEmailError();
          }
        });

        form.addEventListener('submit', (event) => {
          const emailValue = emailInput?.value.trim() || '';

          if (!emailPattern.test(emailValue)) {
            event.preventDefault();
            showEmailError();
            return;
          }

          clearEmailError();

          const formData = new FormData(form);
          const getValue = (name) => (formData.get(name) || '').toString().trim();
          const getAllValues = (name) => formData.getAll(name).map((value) => value.toString().trim()).filter(Boolean);
          const features = getAllValues('feature_interest');
          const featuresSummary = features.length ? features.join(', ') : 'Not specified';

          if (featuresSummaryInput) {
            featuresSummaryInput.value = featuresSummary;
          }

          const summaryLines = [
            'APEX Quote Request',
            '',
            `Name: ${getValue('contact[Name]') || 'Not provided'}`,
            `Phone: ${getValue('contact[Phone]') || 'Not provided'}`,
            `Email: ${getValue('contact[email]') || 'Not provided'}`,
            `Project Address / City: ${getValue('contact[Project Address / City]') || 'Not provided'}`,
            `Product Interest: ${getValue('contact[Product Interest]') || 'Not provided'}`,
            '',
            'Project Dimensions',
            `Approximate Width: ${getValue('contact[Approximate Width]') || 'Not provided'}`,
            `Approximate Depth: ${getValue('contact[Approximate Depth]') || 'Not provided'}`,
            `Approximate Height If Known: ${getValue('contact[Approximate Height If Known]') || 'Not provided'}`,
            `Installation Type: ${getValue('contact[Installation Type]') || 'Not provided'}`,
            '',
            'Color Selection',
            `Preferred Color: ${getValue('contact[Preferred Color]') || 'Not provided'}`,
            `Selected Custom Color: ${getValue('contact[Selected Custom Color]') || 'Not provided'}`,
            '',
            `Features Interested In: ${featuresSummary}`,
            `Preferred Contact Method: ${getValue('contact[Preferred Contact Method]') || 'Not provided'}`,
            '',
            'Project Notes',
            getValue('contact[Project Notes]') || 'Not provided'
          ];

          quoteBodyInput.value = summaryLines.join('\n');
        });

        form.dataset.quoteSummaryInitialized = 'true';
      }

      applyColor(presets[0]?.dataset.quoteColor || colorInput?.value || '#111111', presets[0] || null);
      selector.dataset.quoteColorInitialized = 'true';
    });
  };

  initQuoteColorSelectors();

  const initQuoteSuccessModals = (root = document) => {
    const searchParams = new URLSearchParams(window.location.search);
    const hash = window.location.hash || '';
    const searchShowsContactPost =
      searchParams.get('contact_posted') === 'true' ||
      searchParams.get('customer_posted') === 'true' ||
      searchParams.get('form_type') === 'contact';
    const hashShowsContactPost =
      hash.includes('contact_form') ||
      hash.includes('ContactForm') ||
      hash.includes('ApexQuoteForm') ||
      hash.includes('quote');

    root.querySelectorAll('[data-quote-success-modal]').forEach((modal) => {
      if (modal.dataset.quoteSuccessInitialized === 'true') return;

      const section = modal.closest('.pergola-quote-form-section');
      const dialog = modal.querySelector('.apex-form-success-modal__dialog');
      const inlineSuccess = section?.querySelector('[data-quote-success-inline]');
      const hasErrors = Boolean(section?.querySelector('.pergola-form-error:not([hidden])'));
      const shouldShowModal =
        !hasErrors &&
        (modal.dataset.quoteSuccess === 'true' || Boolean(inlineSuccess) || searchShowsContactPost || hashShowsContactPost);

      const closeModal = () => {
        modal.classList.remove('is-visible');
      };

      const showModal = () => {
        modal.classList.add('is-visible');
        window.requestAnimationFrame(() => {
          dialog?.focus({ preventScroll: true });
        });
      };

      modal.querySelectorAll('[data-quote-success-close]').forEach((trigger) => {
        trigger.addEventListener('click', closeModal);
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && modal.classList.contains('is-visible')) {
          closeModal();
        }
      });

      if (shouldShowModal) {
        showModal();
      }

      modal.dataset.quoteSuccessInitialized = 'true';
    });
  };

  initQuoteSuccessModals();

  let apexRevealObserver = null;

  const showApexReveal = (element) => {
    element.classList.add('is-visible');
  };

  const resetApexReveal = (element) => {
    element.classList.remove('is-visible');
  };

  const initApexReveals = (root = document) => {
    const revealElements = root.querySelectorAll('.apex-reveal');

    if (reduceMotion || !('IntersectionObserver' in window)) {
      revealElements.forEach(showApexReveal);
      return;
    }

    if (!apexRevealObserver) {
      apexRevealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              showApexReveal(entry.target);
            } else {
              resetApexReveal(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.18 }
      );
    }

    revealElements.forEach((element) => {
      if (element.dataset.apexRevealObserved === 'true') return;
      element.dataset.apexRevealObserved = 'true';
      apexRevealObserver.observe(element);
    });
  };

  initApexReveals();
  document.addEventListener('shopify:section:load', (event) => {
    initServiceCarousels(event.target);
    initGalleryCarousels(event.target);
    initGalleryPreviewLinks(event.target);
    initGalleryLightbox();
    initProcessAccordions(event.target);
    initCustomOptions(event.target);
    initQuoteColorSelectors(event.target);
    initQuoteSuccessModals(event.target);
    initApexReveals(event.target);
  });
})();
